import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { EditorState } from 'draft-js'
import cx from 'classnames'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import SubjectEditor from '../SubjectEditor'
import colorBlockPlugin, {
  blockRendererFn,
} from '../../sitewide/RichTextEditor/plugins/colorBlock'
import SubmitMessageButton from './SubmitMessageButton'
import PreviewButton from '../PreviewButton'
import {
  updateRemainingBlastMessages,
  updateRemainingMmsMessages,
} from '../../../state/actions/account'
import * as ConvoMsgActions from '../../../state/actions/conversations-messages'
import {
  sendBlastMessage,
  handleBlastMessageFail,
  getAllBlasts,
} from '../../../state/actions/blast-messages'
import { deactivateSetting } from '../../../state/actions/message-settings'
import { updateCurrConvoHasBody } from '../../../state/actions/current'
import PreviewDestination from '../Blasts/PreviewDestination'
import MessageShutoff from '../Blasts/MessageShutoff'
import MessageAllModal from '../Blasts/MessageAllModal'
import { serializeMessage } from '../../../services/draft-state'
import { Dialog } from 'material-ui'
import css from '../styles/editor.css'
import { logException } from '../../../services/sentry'
import { renderAlert } from '../../../services/render-alert'
import fetchApi from '../../../services/fetchApi'
import characterCountMessage from '../../../services/character-count'
import MessageEditor from './MessageEditor'
import MessageOptionsButton from './MessageOptionsButton'
import ConfirmationModal from './ConfirmationModal'
import CreateTemplateDialog from '../CreateTemplateDialog'
import { prepareSettingForInsert } from './utils'

const editorStateToText = editorState =>
  editorState.getCurrentContent().getPlainText()

class MessagesEditor extends Component {
  static propTypes = {
    messages: PropTypes.arrayOf(PropTypes.object).isRequired,
    attachedMedia: PropTypes.string,
    blastMessagePatients: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ),
    blastMessageDoctors: PropTypes.string,
    blastMessageLocations: PropTypes.string,
    blastMessageDateRange: PropTypes.arrayOf(PropTypes.string),
    conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    countryCode: PropTypes.string,
    deleteTemplate: PropTypes.func.isRequired,
    getBlasts: PropTypes.func.isRequired,
    handleBlastSend: PropTypes.func.isRequired,
    handleBlastFail: PropTypes.func.isRequired,
    hasRemindersTier2: PropTypes.bool,
    hasPatientStatusFilter: PropTypes.bool.isRequired,
    hasApptTypeFilter: PropTypes.bool.isRequired,
    isBlastMsg: PropTypes.bool.isRequired,
    isEmail: PropTypes.bool.isRequired,
    isFocused: PropTypes.bool.isRequired,
    markMessageComplete: PropTypes.func.isRequired,
    maxChars: PropTypes.number.isRequired,
    newConvo: PropTypes.bool,
    remainingBlastMessages: PropTypes.number,
    remainingMmsMessages: PropTypes.number,
    patientFilter: PropTypes.string,
    previewPendingMedia: PropTypes.func.isRequired,
    sendIndividualMessage: PropTypes.func.isRequired,
    templateSettings: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    toggleFocus: PropTypes.func.isRequired,
    unknownNumber: PropTypes.string,
    updateConvoSubject: PropTypes.func,
    updateRemainingBlasts: PropTypes.func.isRequired,
    updateRemainingMms: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props)

    this.state = {
      bodyState: EditorState.createEmpty(),
      subjectState: EditorState.createEmpty(),
      confirmationModalOpen: false,
      isSendingBlastMsg: false,
      isSendingIndivMsg: false,
      mentionableData: this.generateMentionableData,
      messageOptionModalOpen: false,
      pendingTemplateId: null,
      previewMode: false,
      selectedMessageTemplate: null,
      selectionType: null,
      showMessageAllConfirm: false,
      showShutoffOption: false,
      createModalOpen: false,
    }
    this.handleSendPreview = this.handleSendPreview.bind(this)
    this.togglePreview = this.togglePreview.bind(this)
    this.confirmShutoffAndSendMessage =
      this.confirmShutoffAndSendMessage.bind(this)
    this.plugins = [colorBlockPlugin]
  }

  componentWillReceiveProps({ conversationId: nextConvoId }) {
    if (nextConvoId !== this.props.conversationId) {
      this.clearEditorState()
    }
  }

  updateBodyState = bodyState => {
    this.setState({ bodyState })
    this.updateGlobalBodyState(bodyState)
  }

  updateSubjectState = subjectState => {
    this.setState({ subjectState })
  }

  updateGlobalBodyState = updatedBody => {
    const { isBlastMsg, updateEditorHasBody } = this.props
    if (isBlastMsg) return
    updateEditorHasBody(updatedBody.getCurrentContent().hasText())
  }

  clearEditorState = () => {
    this.props.updateEditorHasBody(false)
    this.setState({
      bodyState: EditorState.createEmpty(),
      subjectState: EditorState.createEmpty(),
    })
  }

  toggleFocus = () => {
    this.props.toggleFocus()
  }

  formatEmailSubject = subject => subject.replace(/(\r\n|\n|\r)/gm, '').trim()

  handleSearchChange = ({ value }) => {
    const mentionableData = defaultSuggestionsFilter(
      value,
      this.generateMentionableData,
    )
    this.setState({ mentionableData })
  }

  validateMessage = () => {
    const { bodyState, subjectState } = this.state
    const { isBlastMsg, isEmail, newConvo, attachedMedia, updateConvoSubject } =
      this.props

    // make sure new emails have a subject line
    if (isEmail && (newConvo || isBlastMsg)) {
      const convoSubject = subjectState.getCurrentContent()
      if (!convoSubject.hasText()) {
        renderAlert('Message must have a subject', 'error')
        return false
      }
      if (newConvo) {
        updateConvoSubject(this.formatEmailSubject(convoSubject.getPlainText()))
      }
    }

    // assign message body content & validate
    const bodyText = !bodyState.getCurrentContent().hasText()
      ? null
      : serializeMessage(bodyState, { isEmail })

    if (!bodyText && !attachedMedia) {
      const msg = 'Message must have content'
      renderAlert(msg, 'error')
      return false
    }
    return bodyText
  }

  confirmShutoffAndSendMessage = () => {
    const {
      isBlastMsg,
      patientFilter,
      hasPatientStatusFilter,
      hasApptTypeFilter,
    } = this.props
    if (isBlastMsg && !hasPatientStatusFilter && !hasApptTypeFilter) {
      if (patientFilter === 'all') {
        this.setState({
          showMessageAllConfirm: true,
        })
      } else {
        this.setState({
          showShutoffOption: true,
        })
      }
    } else {
      this.handleSendMessage()
    }
  }

  handleSendMessage = async () => {
    const {
      attachedMedia,
      conversationId,
      isBlastMsg,
      isEmail,
      handleBlastFail,
      markMessageComplete,
    } = this.props
    try {
      const bodyText = this.validateMessage()
      if (!bodyText && !attachedMedia) {
        return
      }

      if (isBlastMsg) {
        await this.promiseBlastMessage(bodyText)
      } else {
        await this.promiseIndividualMessage(bodyText)
      }

      this.clearEditorState()
    } catch (err) {
      handleBlastFail(err, isEmail)
      markMessageComplete(conversationId)
      this.setState({ isSendingBlastMsg: false })
      renderAlert(err.message, 'error')
      logException(err)
    }
  }

  handleSendPreview = to => {
    const { attachedMedia, isEmail } = this.props
    this.togglePreview()
    const body = this.validateMessage()

    if (!body && !attachedMedia) {
      return
    }

    const payload = { body }

    if (isEmail) {
      const url = '/emails'
      const subject = editorStateToText(this.state.subjectState)
      payload.subject = subject
      payload.to = to
      fetchApi(url, 'POST', { body: JSON.stringify({ email: payload }) })
    } else {
      payload.To = to
      payload.Body = payload.body
      payload.media = attachedMedia
      payload.unknown = true
      const url = '/texts'
      fetchApi(url, 'POST', { body: JSON.stringify({ Text: payload }) })
    }
  }

  handleShutoff = () => {
    const {
      blastMessageDoctors,
      blastMessageLocations,
      blastMessageDateRange,
      allLocationsSelected,
      activeLocation,
    } = this.props
    const payload = {
      doctors: blastMessageDoctors,
      locations: blastMessageLocations,
      date_range: blastMessageDateRange,
      all_locations: allLocationsSelected,
      active_location_id: activeLocation && activeLocation.id,
    }
    fetchApi('/message_shutoffs', 'POST', { body: JSON.stringify(payload) })
  }

  togglePreview = () => {
    this.setState({
      previewMode: !this.state.previewMode,
    })
  }

  closeShutoffModal = () => {
    this.setState({
      showShutoffOption: false,
    })
  }

  closeMessageAllModal = () => {
    this.setState({
      showMessageAllConfirm: false,
    })
  }

  promiseBlastMessage = async bodyText => {
    const {
      activeLocation,
      attachedMedia,
      allLocationsSelected,
      blastMessagePatients,
      getBlasts,
      handleBlastSend,
      isEmail,
      patientFilter,
      hasPatientStatusFilter,
      blastMessageLocations,
      updateRemainingBlasts,
      updateRemainingMms,
    } = this.props
    try {
      const subject = !isEmail
        ? null
        : this.formatEmailSubject(editorStateToText(this.state.subjectState))

      const contactMap = blastMessagePatients.reduce(
        (map, pat) => ({
          ...map,
          [pat.id]: pat.contact_delegate,
        }),
        {},
      )
      const locationId = blastMessageLocations ? blastMessageLocations : (activeLocation ? activeLocation.id : null);
      this.setState({ isSendingBlastMsg: true })
      const blastSent = await handleBlastSend({
        bodyText,
        subject,
        isEmail,
        contactMap,
        patientFilter:
          hasPatientStatusFilter && patientFilter === 'all'
            ? 'all_with_status'
            : patientFilter,
        media: attachedMedia,
        locationId,
        allLocations: allLocationsSelected,
      })
      await updateRemainingBlasts(blastSent.remaining_blasts)
      await updateRemainingMms(blastSent.remaining_mms)
      this.setState({ isSendingBlastMsg: false })
      renderAlert('Successfully sent group message', 'success')
      getBlasts()
    } catch (err) {
      logException(err)
    }
  }

  promiseIndividualMessage = async bodyText => {
    const {
      activeLocation,
      attachedMedia,
      conversationId,
      isEmail,
      markMessageComplete,
      newConvo,
      sendIndividualMessage,
      unknownNumber,
      updateRemainingMms,
      previewPendingMedia,
    } = this.props

    this.setState({ isSendingIndivMsg: true })
    try {
      const sentMessage = await sendIndividualMessage({
        isEmailMsg: isEmail,
        conversationId,
        bodyText,
        newConvo,
        media: attachedMedia,
        unknownNumber,
        activeLocationId: activeLocation && activeLocation.id,
      })
      this.setState({ isSendingIndivMsg: false })
      if (sentMessage.media) {
        previewPendingMedia(null)
        const remainingMms = sentMessage.practice.remaining_mms_messages
        await updateRemainingMms(remainingMms)
        renderAlert(
          `Message sent. You have ${sentMessage.practice.remaining_mms_messages} picture messages remaining this month!`,
          'success',
        )
      }
    } catch (err) {
      this.setState({ isSendingIndivMsg: false })
      renderAlert(err.body.error)
      markMessageComplete(conversationId)
      logException(err)
    }
  }

  getTemplate = id => {
    if (this.props.messageSettingsById.hasOwnProperty(id)) {
      return this.props.messageSettingsById[id]
    }
  }

  openOptionsModal = () => {
    this.setState({ messageOptionModalOpen: true })
  }

  closeOptionsModal = () => {
    this.setState({ messageOptionModalOpen: false })
  }

  openCreateTemplateModal = () => {
    this.setState({ createModalOpen: true })
  }

  closeCreateTemplateModal = () => {
    this.setState({ createModalOpen: false, pendingTemplateId: null })
  }

  closeCreateTemplateModalInsert = id => {
    const updatedSetting = this.getTemplate(id)
    this.insertTemplate(updatedSetting)
    this.closeCreateTemplateModal()
  }

  closeConfirmationModal = () => {
    this.setState({ pendingTemplateId: null, selectionType: null })
  }

  handleTemplateSelection = (e, selectionType) => {
    const selectedTemplate = this.getTemplate(+e.target.value)
    if (this.state.messageOptionModalOpen) {
      this.closeOptionsModal()
    }
    if (selectionType === 'insert') {
      this.insertTemplate(selectedTemplate)
      this.closeOptionsModal()
    } else {
      this.setState({
        pendingTemplateId: +e.target.value,
        selectionType: selectionType,
      })
      if (selectionType === 'update') {
        this.openCreateTemplateModal()
      }
    }
  }

  handleTemplateConfirmation = () => {
    const { pendingTemplateId, selectionType } = this.state

    if (selectionType === 'delete') {
      this.props.deleteTemplate(pendingTemplateId)
    }
    this.closeConfirmationModal()
  }

  insertTemplate = selectedTemplate => {
    const {
      isEmail,
      newConvo,
      conversationId,
      conversationsById,
      patient,
      practiceName,
    } = this.props
    const currentConvo = conversationsById[conversationId]
    const { bodyState, subjectState } = prepareSettingForInsert(
      patient,
      currentConvo ? currentConvo.patients : null,
      newConvo,
      selectedTemplate,
      practiceName,
      isEmail,
    )

    this.setState({ bodyState })
    if (subjectState) {
      this.updateSubjectState(subjectState)
    }
  }

  render() {
    const {
      blastMessagePatients,
      isEmail,
      isBlastMsg,
      newConvo,
      maxChars,
      previewPendingMedia,
      countryCode,
      attachedMedia,
      hasRemindersTier2,
      remainingMmsMessages,
      templateSettings,
      practiceName
    } = this.props

    const {
      pendingTemplateId,
      previewMode,
      showShutoffOption,
      showMessageAllConfirm,
      isSendingBlastMsg,
      isSendingIndivMsg,
      subjectState,
      bodyState,
      messageOptionModalOpen,
      selectionType,
      createModalOpen,
    } = this.state

    const isBlastEmail = isEmail && isBlastMsg

    const isTemplateUpdate = selectionType === 'update'

    const emailStyleOpts = {
      [css.blastEmail]: isBlastEmail,
      [css.regularEmail]: !isBlastEmail && isEmail,
    }

    const wrapperCss = cx(css.wrapper, emailStyleOpts)
    const editingAreaCss = cx(css.editingArea, emailStyleOpts)
    const showSubject = isBlastEmail || (newConvo && isEmail)
    const hasBodyText = bodyState.getCurrentContent().hasText()
    const characterCount = bodyState.getCurrentContent().getPlainText().length
    const approachingMaxChars = characterCount > maxChars - 20 && !isEmail
    const exceedsMaxChars = characterCount > maxChars && !isEmail
    const isTops = !!process.env.REACT_APP_IS_TOPS
    const isDisabled =
      (!hasBodyText && !attachedMedia) ||
      previewMode ||
      isSendingBlastMsg ||
      isSendingIndivMsg ||
      exceedsMaxChars
    const hideAttachButton =
      (isBlastMsg &&
        !!blastMessagePatients &&
        remainingMmsMessages < blastMessagePatients.length) ||
      isEmail
    const hideImgPicker =
      countryCode === 'NZ' || isTops || isEmail || !hasRemindersTier2
    const charCountCss = cx(css.characterCount, {
      [css.approachingMaxChars]: approachingMaxChars,
      [css.exceedsMaxChars]: exceedsMaxChars,
    })
    let templateObj = {
      body: bodyState,
      subject: subjectState,
    }
    const savedTemplate = this.getTemplate(pendingTemplateId)
    if (pendingTemplateId) {
      templateObj.id = pendingTemplateId
      templateObj.body = savedTemplate.body
      templateObj.subject = savedTemplate.subject
      templateObj.template_name = savedTemplate.template_name
    }

    return (
      <div
        className={wrapperCss}
        onFocus={this.toggleFocus}
        onBlur={this.toggleFocus}
      >
        <CreateTemplateDialog
          isEmail={isEmail}
          isTemplateUpdate={isTemplateUpdate}
          setting={templateObj}
          templateSettings={templateSettings}
          pendingTemplateId={pendingTemplateId}
          handleTemplateSelection={this.handleTemplateSelection}
          open={createModalOpen}
          closeCreateTemplateModal={this.closeCreateTemplateModal}
          closeCreateTemplateModalInsert={this.closeCreateTemplateModalInsert}
          onRequestClose={this.closeCreateTemplateModal}
        />
        <Dialog
          open={selectionType === 'delete'}
          onRequestClose={this.closeConfirmationModal}
          contentStyle={{ width: '364px' }}
        >
          <ConfirmationModal
            closeConfirmationModal={this.closeConfirmationModal}
            handleTemplateConfirmation={this.handleTemplateConfirmation}
            templateName={templateObj.template_name}
            selectionType={selectionType}
          />
        </Dialog>
        {previewMode && (
          <PreviewDestination
            isEmail={isEmail}
            sendPreview={this.handleSendPreview}
            toggle={this.togglePreview}
          />
        )}
        {showShutoffOption && (
          <MessageShutoff
            {...this.props}
            handleShutoff={this.handleShutoff}
            handleSendMessage={this.handleSendMessage}
            close={this.closeShutoffModal}
            isSendingBlastMsg={isSendingBlastMsg}
          />
        )}
        {showMessageAllConfirm && (
          <MessageAllModal
            {...this.props}
            handleSendMessage={this.handleSendMessage}
            close={this.closeMessageAllModal}
            isSendingBlastMsg={isSendingBlastMsg}
          />
        )}
        {isBlastMsg && (
          <PreviewButton isEmail={isEmail} toggle={this.togglePreview} />
        )}
        <SubmitMessageButton
          handleSendMessage={this.confirmShutoffAndSendMessage}
          isDisabled={isDisabled}
          isSendingBlastMsg={isSendingBlastMsg}
          isSendingIndivMsg={isSendingIndivMsg}
        />
        {!hideAttachButton && (
          <MessageOptionsButton
            openCreateTemplateModal={this.openCreateTemplateModal}
            createModalOpen={createModalOpen}
            handleTemplateSelection={this.handleTemplateSelection}
            isBlastMsg={isBlastMsg}
            isEmail={isEmail}
            hideImgPicker={hideImgPicker}
            previewPendingMedia={previewPendingMedia}
            openOptionsModal={this.openOptionsModal}
            optionsModalOpen={messageOptionModalOpen}
            closeOptionsModal={this.closeOptionsModal}
            templateSettings={templateSettings}
          />
        )}
        <div className={editingAreaCss}>
          {showSubject && (
            <div className={css.subject}>
              <SubjectEditor
                subjectState={subjectState}
                updateSubjectState={this.updateSubjectState}
              />
            </div>
          )}
          <MessageEditor
            messages={this.props.messages}
            blockRendererFn={isBlastEmail ? blockRendererFn : null}
            bodyState={bodyState}
            closeOptionsModal={this.closeOptionsModal}
            createModalOpen={createModalOpen}
            handleSearchChange={this.handleSearchChange}
            handleTemplateSelection={this.handleTemplateSelection}
            hideImgPicker={hideImgPicker}
            isEmail={isEmail}
            isBlastEmail={isBlastMsg}
            onChange={this.updateBodyState}
            openCreateTemplateModal={this.openCreateTemplateModal}
            openOptionsModal={this.openOptionsModal}
            optionsModalOpen={messageOptionModalOpen}
            placeholder="Type your message here..."
            plugins={this.plugins}
            showExtraButtons={isBlastEmail}
            templateSettings={templateSettings}
            updateBodyState={this.updateBodyState}
            practiceName={practiceName}
          />
        </div>
        {!isEmail && hasBodyText && (
          <div className={charCountCss}>
            {characterCountMessage(characterCount, maxChars)}
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = ({
  account: {
    user: { countryCode, activeLocation, practiceName },
    auth: { remainingBlastMessages, remainingMmsMessages },
  },
  blastMessages: {
    unsent: {
      addresseeContactInfo,
      selectedDoctorId,
      selectedLocationId,
      dateRange,
      allLocationsSelected,
    },
  },
  conversations: { creatingNewConversation: newConvo, byId: conversationsById },
  current: {
    conversation: { id: conversationId, isEmail, isBlastMsg },
  },
  messages: { dataByConversation },
  patientGroups: {
    doctors,
    locations,
    selectedMenu,
    hasStatusFilter,
    hasApptTypeFilter,
  },
  selectedPatient: { patient },
}) => {
  let {
    blastMessagePatients,
    blastMessageDateRange,
    blastMessageDoctors,
    blastMessageLocations,
    doctor,
    location,
    patientFilter,
  } = []
  const maxChars = ['US', 'CA'].includes(countryCode) ? 480 : 320

  if (isBlastMsg) {
    blastMessagePatients = addresseeContactInfo.patients
    blastMessageDoctors = selectedDoctorId
    blastMessageLocations = selectedLocationId
    blastMessageDateRange = dateRange
    doctor = doctors.filter(doc => doc.id === blastMessageDoctors)
    location = locations.filter(loc => loc.id === blastMessageLocations)
    patientFilter = selectedMenu.id
  }

  const attachedMedia = dataByConversation.hasOwnProperty(conversationId)
    ? dataByConversation[conversationId].attachedMedia
    : null

  return {
    activeLocation,
    allLocationsSelected,
    attachedMedia,
    blastMessageDateRange,
    blastMessageDoctors,
    blastMessageLocations,
    blastMessagePatients,
    conversationId,
    conversationsById,
    countryCode,
    doctor,
    isBlastMsg,
    isEmail,
    hasApptTypeFilter,
    hasPatientStatusFilter: hasStatusFilter,
    location,
    maxChars,
    newConvo,
    patient,
    patientFilter,
    practiceName,
    remainingBlastMessages,
    remainingMmsMessages,
  }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      handleBlastSend: sendBlastMessage,
      handleBlastFail: handleBlastMessageFail,
      sendIndividualMessage: ConvoMsgActions.sendMessage,
      markMessageComplete: ConvoMsgActions.markMessageComplete,
      updateConvoSubject: ConvoMsgActions.addSubjectToConversation,
      getBlasts: getAllBlasts,
      updateRemainingBlasts: updateRemainingBlastMessages,
      updateRemainingMms: updateRemainingMmsMessages,
      updateEditorHasBody: hasBody => updateCurrConvoHasBody(hasBody),
      deleteTemplate: id => deactivateSetting(id),
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(MessagesEditor)



// WEBPACK FOOTER //
// ./src/components/Messages/MessagesEditor/index.js